/* eslint-disable react/prop-types */
import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import Draggable from 'react-draggable'

import { useHyperspace } from '@acs-manageability/webrtc-react'

import { ToolbarButton } from './ToolbarButton'
import { Telemetry } from './Telemetry'
import './styles.scss'

const defaultButtonsTitles = {
  controlAltDel: 'Ctrl Alt Del',
  disconnect: 'Disconnect',
  drag: 'Drag',
  mute: 'Mute',
  unmute: 'Unmute',
  pin: 'Pin',
  unpin: 'Unpin',
  fullscreenMode: 'Fullscreen mode',
  windowMode: 'Window mode',
  pointerLock: 'Pointer Lock',
}

export function WebRTCToolbar({
  renderCtrlAltDelButton,
  renderPointerLockButton,
  buttonsTitles = defaultButtonsTitles,
  renderLatency,
  onFullscreenToggleRequest,
}) {
  const toolbarRef = useRef(null)

  const {
    disconnect,
    isAudioDisabled,
    isMuted,
    isRelative,
    sendCtrlAltDel,
    setIsRelative,
    toggleMuteAudio,
    fullscreenMode,
    getVideoRef,
    setFullscreenMode,
    toggleFullscreenMode,
    setDefaultCursor,
  } = useHyperspace((state) => ({
    disconnect: state.disconnect,
    isAudioDisabled: state.isAudioDisabled,
    isMuted: state.isMuted,
    isRelative: state.isRelative,
    sendCtrlAltDel: state.sendCtrlAltDel,
    setIsRelative: state.setIsRelative,
    toggleMuteAudio: state.toggleMuteAudio,
    fullscreenMode: state.fullscreenMode,
    getVideoRef: state.getVideoRef,
    setFullscreenMode: state.setFullscreenMode,
    toggleFullscreenMode: state.toggleFullscreenMode,
    setDefaultCursor: state.setDefaultCursor,
  }))

  const [leftOffset, setLeftOffset] = useState(0)
  const [isPinnedActive, setIsPinnedActive] = useState(false)
  const [isHovered, setIsHovered] = useState(true)

  // Remembers the pre-fullscreen horizontal position so it can be restored on exit
  // instead of drifting from the fullscreen-time canvas clamping (see updateToolbarPosition).
  const leftOffsetRef = useRef(0)
  const wasFullscreenRef = useRef(false)
  const savedLeftOffsetRef = useRef(0)

  useEffect(() => {
    leftOffsetRef.current = leftOffset
  }, [leftOffset])

  const checkFullscreenMode = useCallback(() => {
    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight
    const screenWidth = screen.width
    const screenHeight = screen.height

    if (windowWidth === screenWidth && windowHeight === screenHeight) {
      setFullscreenMode(true)
    } else if (windowWidth < screenWidth || windowHeight < screenHeight) {
      setFullscreenMode(false)
    }
  }, [setFullscreenMode])

  const lockChange = useCallback(() => {
    const ref = getVideoRef?.()
    if (ref?.current) {
      setIsRelative(document.pointerLockElement === ref.current)
    }
  }, [getVideoRef, setIsRelative])

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsHovered(false)
    }, 2000)
    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    document.addEventListener('pointerlockchange', lockChange, false)
    return () => {
      document.removeEventListener('pointerlockchange', lockChange, false)
    }
  }, [lockChange])

  const updateToolbarPosition = useCallback(() => {
    const canvas = document.querySelector('#canvas')
    if (!canvas || !toolbarRef.current) {
      return
    }

    const matrix = new DOMMatrix(getComputedStyle(toolbarRef.current).transform)
    const translateX = matrix.m41

    if (canvas.getBoundingClientRect().right < toolbarRef.current.getBoundingClientRect().right) {
      setLeftOffset(
        canvas.getBoundingClientRect().right -
          toolbarRef.current.getBoundingClientRect().width -
          translateX,
      )
    }

    if (canvas.getBoundingClientRect().left > toolbarRef.current.getBoundingClientRect().left) {
      setLeftOffset(canvas.getBoundingClientRect().left - translateX)
    }
  }, [])

  useEffect(() => {
    const resizeCallback = () => {
      const isNowFullscreen =
        window.innerWidth === screen.width && window.innerHeight === screen.height
      const wasFullscreen = wasFullscreenRef.current

      if (isNowFullscreen && !wasFullscreen) {
        savedLeftOffsetRef.current = leftOffsetRef.current
      }

      checkFullscreenMode()

      if (!isNowFullscreen && wasFullscreen) {
        // Just exited fullscreen: restore the position it had beforehand rather than
        // re-clamping against the canvas, which drifts away from the original spot.
        setLeftOffset(savedLeftOffsetRef.current)
      } else {
        updateToolbarPosition()
      }

      wasFullscreenRef.current = isNowFullscreen
    }

    window.addEventListener('resize', resizeCallback)
    return () => {
      window.removeEventListener('resize', resizeCallback)
    }
  }, [checkFullscreenMode, updateToolbarPosition])

  useLayoutEffect(() => {
    if (toolbarRef.current?.offsetWidth) {
      setLeftOffset(Math.abs((window.innerWidth - toolbarRef.current.offsetWidth) / 2))
    }
  }, [])

  const onMouseEnter = useCallback(() => {
    setDefaultCursor?.(toolbarRef.current)
    setIsHovered(true)
  }, [setDefaultCursor])

  const onMouseLeave = useCallback(() => {
    setTimeout(() => {
      setIsHovered(false)
    }, 1500)
  }, [])

  const handleFullscreenClick = useCallback(() => {
    const nextState = !fullscreenMode
    if (onFullscreenToggleRequest) {
      const handled = onFullscreenToggleRequest(nextState)
      if (handled) {
        setFullscreenMode(nextState)
        return
      }
    }

    toggleFullscreenMode?.().catch(() => null)
  }, [fullscreenMode, onFullscreenToggleRequest, setFullscreenMode, toggleFullscreenMode])

  return (
    <Draggable axis="x" nodeRef={toolbarRef} handle=".handle" bounds="parent">
      <div
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        className={`toolbar ${isPinnedActive || isHovered ? 'pinned' : ''} ${isRelative?.() ? 'hidden' : ''}`}
        data-testid="toolbar"
        style={{ left: leftOffset }}
        ref={toolbarRef}
      >
        <ToolbarButton
          data-testid="Drag"
          title={buttonsTitles.drag}
          className="handle"
          icon="DragIndicator"
        />

        <ToolbarButton
          data-testid="pin"
          title={isPinnedActive ? buttonsTitles.unpin : buttonsTitles.pin}
          className="container"
          icon="PushPin"
          active={isPinnedActive}
          onClick={() => setIsPinnedActive((oldState) => !oldState)}
        />

        <Telemetry renderLatency={renderLatency} />

        <ToolbarButton
          data-testid="Fullscreen mode"
          title={fullscreenMode ? buttonsTitles.windowMode : buttonsTitles.fullscreenMode}
          className="container"
          active={fullscreenMode}
          icon={fullscreenMode ? 'FullscreenExit' : 'Fullscreen'}
          onClick={handleFullscreenClick}
        />

        {!isAudioDisabled && (
          <ToolbarButton
            data-testid="Mute"
            title={isMuted ? buttonsTitles.unmute : buttonsTitles.mute}
            className="container"
            icon={isMuted ? 'VolumeOff' : 'VolumeUp'}
            onClick={() => toggleMuteAudio?.()}
          />
        )}

        {renderCtrlAltDelButton && (
          <ToolbarButton
            data-testid="Ctrl Alt Del"
            title={buttonsTitles.controlAltDel}
            className="container ctrlAltDel"
            icon="ctrlAltDel"
            onClick={() => sendCtrlAltDel?.()}
          />
        )}

        {renderPointerLockButton && (
          <ToolbarButton
            data-testid="Pointer Lock"
            title={buttonsTitles.pointerLock}
            className="container"
            icon="pointerLock"
            onClick={() => getVideoRef?.()?.current?.requestPointerLock?.()}
          />
        )}

        <ToolbarButton
          data-testid="Disconnect"
          title={buttonsTitles.disconnect}
          className="container"
          icon="Logout"
          onClick={() => disconnect?.()}
        />
      </div>
    </Draggable>
  )
}
